import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { Play, Clock, BookOpen, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MyOnlineCourses = () => {
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMyCourses();
  }, []);

  const fetchMyCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/online-enrollments/my-courses`);
      setEnrollments(response.data);
    } catch (error) {
      toast.error('Error al cargar tus cursos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status, progreso) => {
    if (status === 'completado' || progreso >= 100) {
      return <Badge className="bg-green-100 text-green-800">Completado</Badge>;
    }
    if (progreso > 0) {
      return <Badge className="bg-blue-100 text-blue-800">En progreso</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-800">No iniciado</Badge>;
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes} min`;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-manrope font-bold text-3xl text-slate-900">Mis Cursos</h1>
          <p className="font-inter text-slate-600 mt-1">Tu progreso en formación online</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{enrollments.length}</p>
                <p className="text-sm text-slate-500">Cursos inscritos</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {enrollments.filter(e => e.status === 'completado' || e.progreso >= 100).length}
                </p>
                <p className="text-sm text-slate-500">Completados</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {formatTime(enrollments.reduce((acc, e) => acc + (e.tiempo_total || 0), 0))}
                </p>
                <p className="text-sm text-slate-500">Tiempo total</p>
              </div>
            </div>
          </div>
        </div>

        {/* Courses List */}
        <div className="space-y-4">
          {enrollments.map((enrollment) => (
            <div
              key={enrollment.id}
              className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="flex">
                {/* Image */}
                <div className="w-48 h-36 bg-gradient-to-br from-[#004E89] to-[#0EA5E9] flex-shrink-0">
                  {enrollment.course?.imagen_url ? (
                    <img
                      src={enrollment.course.imagen_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${enrollment.course.imagen_url}` : enrollment.course.imagen_url}
                      alt={enrollment.course.nombre}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <BookOpen className="w-12 h-12 text-white/50" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-manrope font-semibold text-lg text-slate-900">
                        {enrollment.course?.nombre}
                      </h3>
                      <p className="text-sm text-slate-500 mt-1 line-clamp-1">
                        {enrollment.course?.descripcion}
                      </p>
                    </div>
                    {getStatusBadge(enrollment.status, enrollment.progreso)}
                  </div>

                  {/* Progress */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-slate-600">Progreso</span>
                      <span className="font-medium text-slate-900">{Math.round(enrollment.progreso)}%</span>
                    </div>
                    <Progress value={enrollment.progreso} className="h-2" />
                  </div>

                  {/* Footer */}
                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatTime(enrollment.tiempo_total || 0)} dedicado
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="bg-[#004E89] hover:bg-[#003B66]"
                      onClick={() => navigate(`/online-courses/${enrollment.online_course_id}`)}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {enrollment.progreso > 0 ? 'Continuar' : 'Empezar'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {enrollments.length === 0 && (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900">No estás inscrito en ningún curso</h3>
            <p className="text-slate-500 mt-1 mb-4">Explora el catálogo de cursos disponibles</p>
            <Button
              onClick={() => navigate('/online-courses')}
              className="bg-[#004E89] hover:bg-[#003B66]"
            >
              Ver cursos disponibles
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default MyOnlineCourses;
